import React, { FC, Fragment, useEffect, useMemo, useState } from 'react'
import {
	ArrowBack,
	CopyAll,
	DriveFileMoveOutlined,
	Folder,
	Star,
	StarOutline,
	Visibility,
} from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { Button, Dialog, IconButton } from '../../ui'
import { DocumentData } from '../../../lib/api/types/document.interfaces'
import {
	useCopyObject,
	useFavorites,
	useGetFolder,
	useMoveObject,
} from '../../../hooks/api/documentHooks'
import { getShortenPath } from '../../../lib/utils/formatDocument'
import WorkspaceSwitcher from '../../../pages/Workspace/components/WorkspaceSwitcher'
import { WorkspaceType } from '../../../pages/Workspace/components/interfaces.table'
import LockerLoader from '../../common/LockerLoader'
import cn from '../../../lib/utils/cn'
import FileFormatIcon from '../../common/FileFormatIcon'
import { ReactComponent as FolderPlus } from '../../../assets/icons/interactions/folder_plus.svg'
import { ReactComponent as FolderMinus } from '../../../assets/icons/interactions/folder_minus.svg'
import { useProgressContext } from '../../../context/progressContext'
import ObjectActionConflictModal from './ObjectActionConflictModal'
import { useAuth } from '../../../hooks'

export type ObjectActionType = 'move' | 'copy' | 'select' | null

interface ObjectActionModalProps {
	isOpen: boolean
	onClose: () => void
	onActionCallback?: () => void
	actionType: ObjectActionType
	openedFolders?: string[]
	currentObject?: DocumentData | null
	currentObjects?:
	| {
		id: string
		type: number
		name?: string
		fileType?: string
	}[]
	| null
	withoutInvalidate?: boolean
	sourceFolderId?: string | null
}

const ObjectActionModal: FC<ObjectActionModalProps> = ({
	isOpen,
	onClose,
	onActionCallback,
	actionType,
	openedFolders,
	currentObject,
	currentObjects,
	withoutInvalidate = false,
	sourceFolderId,
}) => {
	const { isAuthenticated, currentUser } = useAuth()
	const { setMoveCopyStatuses, openMoveCopyProgress } = useProgressContext()

	const { watch, setValue, reset } = useForm<{
		selectedFolders: { id: string; name: string }[]
		selectedFolder: { id: string; name: string } | null
		isFavorites: boolean
		workspace: WorkspaceType
	}>({
		defaultValues: {
			selectedFolders: [],
			selectedFolder: null,
			isFavorites: false,
			workspace: 'personal',
		},
	})

	const { selectedFolders, selectedFolder, isFavorites, workspace } = watch()

	useEffect(() => {
		if (!isOpen) return
		if (
			selectedFolder?.id !== selectedFolders[selectedFolders?.length - 1]?.id
		) {
			setValue(
				'selectedFolder',
				selectedFolders?.length
					? selectedFolders[selectedFolders?.length - 1]
					: null
			)
		}
	}, [selectedFolders])

	useEffect(() => {
		if (!isOpen) return
		setValue('selectedFolders', [])
	}, [workspace])

	const currentId = useMemo(
		() =>
			selectedFolders[selectedFolders?.length - 1]?.id ||
			(workspace === 'organization'
				? currentUser?.organization?.sharedFolderId
				: currentUser?.userFolderId),
		[
			selectedFolders,
			workspace,
			currentUser?.organization?.sharedFolderId,
			currentUser?.userFolderId,
		]
	)

	const destinationId = selectedFolder?.id || currentId

	const [conflictedObjects, setConflictedObjects] = useState<any[]>([])

	const { mutateAsync: copyObject, isPending: isCopying } = useCopyObject()
	const { mutateAsync: moveObject, isPending: isMoving } = useMoveObject()

	const {
		data: allItems,
		isLoading,
		isFetched,
	} = useGetFolder(
		{
			id: currentId ?? '',
		},
		{
			enabled: Boolean(isOpen && currentId && !isFavorites),
			staleTime: 1000 * 10,
		}
	)

	const {
		data: favoriteItems,
		isLoading: isLoadingFavorites,
		isFetched: isFetchedFavorites,
	} = useFavorites(
		{},
		{ enabled: Boolean(isFavorites && isAuthenticated && isOpen), staleTime: 1000 * 10 }
	)

	const handleClose = () => {
		onClose()
		reset()
		setConflictedObjects([])
	}

	useEffect(() => {
		if (isOpen) {
			setConflictedObjects([])
		}
	}, [isOpen])

	const isPending = isCopying || isMoving

	const openedFoldersString = openedFolders ? getShortenPath(openedFolders) : ''

	const currentData = useMemo(
		() => (isFavorites ? favoriteItems?.data : allItems?.data?.items),
		[isFavorites, favoriteItems, allItems]
	)

	const handleSubmit = async (_selectedItems?: any[]) => {
		if (!destinationId) return

		const operationId = `op_${Date.now()}_${Math.random()
			.toString(36)
			.substr(2, 9)}_${Math.random().toString(36).substr(2, 9)}`

		const onSuccessCallback = () => {
			onClose()
			reset()
			setConflictedObjects([])
			onActionCallback?.()
		}

		const onErrorCallback = (error: any) => {
			const errorMessage =
				error?.response?.data?.message || error?.message || ''
			const errorCode =
				error?.response?.data?.errors?.[0] ||
				error?.response?.data?.errorMessageCode
			const isDuplicateError =
				errorCode === 'FILE_SAME_NAME_EXISTS' ||
				errorCode === 'OBJECT_ALREADY_EXISTS' ||
				errorMessage.toLowerCase().includes('already exists') ||
				errorMessage.toLowerCase().includes('duplicate') ||
				error?.response?.status === 409

			if (isDuplicateError) {
				const conflictItems =
					currentObjects ?? (currentObject ? [currentObject] : [])
				setConflictedObjects(conflictItems)
			}
		}

		const initializeProgress = (
			items: Array<{ id: string; name: string; type: number }>
		) => {
			openMoveCopyProgress()

			const newItems = items.map((item) => ({
				id: item.id,
				name: item.name || 'Unknown',
				type: item.type,
				status: 'pending' as const,
				operationId,
				operationType: (actionType === 'copy' || actionType === 'move'
					? actionType
					: 'copy') as 'copy' | 'move',
				parentId: destinationId,
				sourceFolderId: actionType === 'move' ? sourceFolderId : undefined,
			}))

			setMoveCopyStatuses(newItems)
		}

		const onProgress = (
			itemId: string,
			status: 'processing' | 'completed' | 'failed',
			error?: string
		) => {
			const currentOperationId = operationId
			setMoveCopyStatuses((prev) =>
				prev.map((item) => {
					if (item.id === itemId && item.operationId === currentOperationId) {
						if (item.status === 'completed' && status === 'failed') {
							return item
						}
						if (
							item.status === 'failed' &&
							status === 'completed' &&
							item.operationId !== currentOperationId
						) {
							return item
						}
						return { ...item, status, error }
					}
					return item
				})
			)
		}

		if (currentObject?.id) {
			initializeProgress([
				{
					id: currentObject.id,
					name: currentObject.name || 'Unknown',
					type: currentObject.type || 0,
				},
			])

			const data = {
				data: {
					id: currentObject?.id,
					type: currentObject?.type,
					name: currentObject?.name,
				},
				parentId: destinationId,
				invalidate: !withoutInvalidate,
				onProgress,
				onErrorCallback,
				sourceFolderId: actionType === 'move' ? sourceFolderId : undefined,
				autoRename: false,
			}

			const result =
				actionType === 'copy' ? await copyObject(data) : await moveObject(data)

			if (result?.data?.success) {
				onSuccessCallback()
			}
			return
		}

		const currentItems = currentObjects // selectedItems

		if (currentItems?.length) {
			const itemsWithNames = currentItems.map((item) => ({
				id: item.id,
				name:
					item.name ||
					`New ${item.type === 1 ? 'Folder' : 'File'} for ${new Date()}`,
				type: item.type || 0,
			}))
			initializeProgress(itemsWithNames)

			const results = await Promise.all(
				currentItems?.map((item) => {
					const itemData = {
						data: {
							id: item.id,
							type: item.type || 0,
							name:
								item.name ||
								`New ${item.type === 1 ? 'Folder' : 'File'} for ${new Date()}`,
						},
						parentId: destinationId,
						invalidate: !withoutInvalidate,
						onProgress,
						onErrorCallback,
						sourceFolderId: actionType === 'move' ? sourceFolderId : undefined,
						autoRename: false,
					}

					return actionType === 'copy'
						? copyObject(itemData)
						: moveObject(itemData)
				})
			)

			if (results.every((result) => result?.data?.success)) {
				onSuccessCallback()
			}
		}
	}

	const titleGenerator = () => {
		if (actionType === 'copy') {
			return currentObject?.fileType === 'Folder' ? 'Copy Folder' : 'Copy File'
		} else if (actionType === 'move') {
			return currentObject?.fileType === 'Folder' ? 'Move Folder' : 'Move File'
		}
		return ''
	}

	if (!isOpen) return null

	return (
		<Fragment>
			<Dialog
				isOpen={isOpen}
				onClose={handleClose}
				title={titleGenerator()}
				icon={actionType == 'copy' ? <CopyAll /> : <DriveFileMoveOutlined />}
				className="w-[480px]"
				controllers={
					<div className="flex items-center justify-end gap-2">
						<Button variant="tertiary" onClick={handleClose} label="Cancel" />
						<Button
							label={`${actionType == 'copy' ? 'Copy' : 'Move'} ${selectedFolder?.id ? `to ${selectedFolder?.name}` : ''
								}`}
							onClick={() => handleSubmit()}
							loading={isPending}
							disabled={!destinationId}
						/>
					</div>
				}
			>
				<div className="flex flex-col gap-2">
					{currentObject?.id ? (
						<p className="flex items-center justify-normal gap-1 text-text-table dark:text-text-table-dark">
							<FileFormatIcon fileName={currentObject?.name} size={20} />
							{currentObject?.name}
						</p>
					) : (
						<p>{currentObjects?.length} items</p>
					)}
					{!!openedFolders?.length && (
						<p className="text-sm text-text-table dark:text-text-table-dark font-bold">
							Current:
							<span className="font-normal"> ../{openedFoldersString}</span>
						</p>
					)}
				</div>

				{!!currentUser?.organization?.id && (
					<WorkspaceSwitcher
						workspace={workspace}
						onChange={(type: WorkspaceType) => setValue('workspace', type)}
						className="w-full flex items-center justify-start mt-5 mb-3"
						disabled={isFavorites}
					/>
				)}

				<div className="rounded-lg border border-stroke-light dark:border-stroke-light-dark ">
					<div className="h-12 px-2 flex items-center justify-between border-b border-stroke-light dark:border-stroke-light-dark ">
						<div className="text-base flex items-center justify-start gap-2 text-text-table dark:text-text-table-dark">
							{!!selectedFolders?.length && (
								<IconButton
									className="w-8 h-8"
									variant="secondary"
									icon={<ArrowBack />}
									onClick={() => {
										const newSelectedFolders = [...selectedFolders]
										newSelectedFolders.pop()
										setValue('selectedFolders', newSelectedFolders)
										setValue(
											'selectedFolder',
											newSelectedFolders[newSelectedFolders.length - 1] || null
										)
									}}
								/>
							)}
							{selectedFolders[selectedFolders?.length - 1]?.name ?? '../'}
						</div>
						<IconButton
							variant={isFavorites ? 'primary' : 'secondary'}
							icon={
								isFavorites ? (
									<Star className="dark:text-text-main-dark" />
								) : (
									<StarOutline className="dark:text-text-main-dark" />
								)
							}
							onClick={() => {
								setValue('isFavorites', !isFavorites)
								setValue('selectedFolders', [])
								setValue('workspace', 'personal')
							}}
							tooltip="Favorites"
							className="w-8 h-8"
						/>
					</div>
					<div className="h-52 relative overflow-y-auto">
						<LockerLoader visible={isLoading || isLoadingFavorites} />
						{(isFavorites ? isFetchedFavorites : isFetched) &&
							!currentData?.pagination?.totalCount ? (
							<div className="flex items-center justify-center h-full w-full">
								<p className="text-text-secondary italic">No folders here...</p>
							</div>
						) : (
							currentData?.entries
								?.filter(
									(item) =>
										item?.type &&
										!(
											currentObject?.id &&
											item?.id === currentObject?.id &&
											currentObject?.fileType === 'Folder'
										) &&
										!(
											currentObjects?.length &&
											currentObjects.some(
												(obj) => obj.id === item?.id && obj.type === 1
											)
										)
								)
								.map(({ name, id }, index) => {
									const isSelected = selectedFolder?.id == id
									const currentFolder =
										selectedFolders[selectedFolders?.length - 1] || null
									return (
										<div
											key={id}
											onDoubleClick={() => {
												!isFavorites &&
													setValue('selectedFolders', [
														...selectedFolders,
														{ name, id },
													])
											}}
											onClick={() =>
												setValue(
													'selectedFolder',
													isSelected ? currentFolder : { name, id }
												)
											}
											className={cn(
												'h-16 flex items-center group justify-between gap-2 px-5 dark:text-text-main-dark hover:cursor-pointer hover:bg-background-light-hover dark:hover:bg-background-dark-hover duration-default select-none	 ',
												!(index % 2) &&
												'bg-background-light dark:bg-background-light-dark dark:hover:bg-background-dark-hover',
												isSelected &&
												'bg-accent-light dark:bg-accent-hover/30 hover:bg-accent-light dark:hover:bg-accent-hover/50'
											)}
										>
											<p className="flex items-center justify-normal gap-2">
												<Folder
													className={cn(
														'fill-text-secondary',
														isSelected && 'fill-accent'
													)}
												/>
												{name}
											</p>
											<div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 duration-default">
												<IconButton
													className="w-8 h-8 bg-white hover:bg-white dark:bg-background-dark group/button border-none duration-default"
													variant="tertiary"
													icon={
														isSelected ? (
															<FolderMinus
																className={cn(
																	'fill-text-secondary group-hover/button:fill-text-main duration-default',
																	isSelected &&
																	'fill-accent group-hover/button:fill-accent-hover'
																)}
															/>
														) : (
															<FolderPlus
																className={cn(
																	'fill-text-secondary group-hover/button:fill-text-main duration-default',
																	isSelected &&
																	'fill-accent group-hover/button:fill-accent-hover'
																)}
															/>
														)
													}
													onClick={() =>
														setValue(
															'selectedFolder',
															isSelected ? currentFolder : { name, id }
														)
													}
													tooltip={isSelected ? 'Unselect' : 'Select'}
												/>
												{!isFavorites && (
													<IconButton
														className="w-8 h-8 bg-white hover:bg-white dark:bg-background-dark group/button border-none duration-default"
														variant="tertiary"
														icon={
															<Visibility
																className={cn(
																	'fill-text-secondary group-hover/button:fill-text-main duration-default',
																	isSelected &&
																	'fill-accent group-hover/button:fill-accent-hover'
																)}
															/>
														}
														tooltip="Open folder"
														onClick={() =>
															isFavorites
																? setValue(
																	'selectedFolder',
																	isSelected ? currentFolder : { name, id }
																)
																: setValue('selectedFolders', [
																	...selectedFolders,
																	{ name, id },
																])
														}
													/>
												)}
											</div>
										</div>
									)
								})
						)}
					</div>
				</div>
			</Dialog>

			<ObjectActionConflictModal
				isOpen={!!conflictedObjects?.length}
				onClose={() => setConflictedObjects([])}
				actionType={actionType}
				conflictedObjects={conflictedObjects}
				onAction={() => { }}
			/>
		</Fragment>
	)
}

export default ObjectActionModal
